import { Stack, TextField } from "@mui/material";
import { useRateConfiguration } from "@src/appV2/ExperimentalHomeHealth/lib/useRateConfiguration";
import { Text } from "@src/appV2/redesign/components/Text";

import { PricingType, type VisitType, type WorkerRequirements } from "../types";

export interface Step2Data {
  visitsPerWeek: string;
  totalWeeks: string;
  firstVisitDueDate: string;
  chargePerVisit: string;
  payPerVisit: string;
}

interface Props {
  data: Step2Data;
  pricingType: PricingType;
  visitType: VisitType | undefined;
  workerRequirement: WorkerRequirements | undefined;
  patientAddress: string;
  onChange: (field: keyof Step2Data, value: string) => void;
}

const inputSx = {
  bgcolor: "background.paper",
  borderRadius: 2,
  "& .MuiOutlinedInput-root": { borderRadius: 2 },
  width: "100%",
};

export function MobileCreateCaseStep2({
  data,
  pricingType,
  visitType,
  workerRequirement,
  patientAddress,
  onChange,
}: Props) {
  const rateLabel = pricingType === PricingType.PER_HOUR ? "per hour" : "per visit";
  const rates = useRateConfiguration({
    visitType,
    workerRequirements: workerRequirement,
    pricingType,
    patientAddress,
  });

  function handleChargeChange(value: string) {
    onChange("chargePerVisit", value);
    if (rates.canSetRates && value !== "") {
      const charge = Number.parseFloat(value);
      if (!Number.isNaN(charge)) {
        const takeRate = rates.takePercent / 100;
        if (takeRate < 0 || takeRate >= 1) {
          onChange("payPerVisit", "");
          return;
        }
        const pay = (charge * (1 - takeRate)).toFixed(2);
        onChange("payPerVisit", pay);
      }
    }
  }

  function handlePayChange(value: string) {
    onChange("payPerVisit", value);
    if (rates.canSetRates && value !== "") {
      const pay = Number.parseFloat(value);
      if (!Number.isNaN(pay)) {
        const takeRate = rates.takePercent / 100;
        if (takeRate < 0 || takeRate >= 1) {
          onChange("chargePerVisit", "");
          return;
        }
        const charge = (pay / (1 - takeRate)).toFixed(2);
        onChange("chargePerVisit", charge);
      }
    }
  }

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Text semibold variant="body1">
          Visits per week
        </Text>
        <TextField
          type="number"
          value={data.visitsPerWeek}
          onChange={(e) => {
            onChange("visitsPerWeek", e.target.value);
          }}
          sx={inputSx}
          inputProps={{ min: 1 }}
        />
      </Stack>

      <Stack spacing={1}>
        <Text semibold variant="body1">
          Total weeks
        </Text>
        <TextField
          type="number"
          value={data.totalWeeks}
          onChange={(e) => {
            onChange("totalWeeks", e.target.value);
          }}
          sx={inputSx}
          inputProps={{ min: 1 }}
        />
      </Stack>

      <Stack spacing={1}>
        <Text semibold variant="body1">
          First visit due date
        </Text>
        <TextField
          type="date"
          value={data.firstVisitDueDate}
          onChange={(e) => {
            onChange("firstVisitDueDate", e.target.value);
          }}
          sx={inputSx}
        />
      </Stack>

      {rates.canSetRates && (
        <>
          <Stack spacing={1}>
            <Text semibold variant="body1">{`Charge ${rateLabel}`}</Text>
            <TextField
              type="number"
              placeholder={`${rates.chargeRate.recommendedRate.toFixed(2)} (recommended)`}
              value={data.chargePerVisit}
              onChange={(e) => {
                handleChargeChange(e.target.value);
              }}
              sx={inputSx}
              inputProps={{
                min: rates.chargeRate.minimum,
                max: rates.chargeRate.maximum,
                step: 0.01,
              }}
            />
          </Stack>

          <Stack spacing={1}>
            <Text semibold variant="body1">{`Pay ${rateLabel}`}</Text>
            <TextField
              type="number"
              placeholder={rates.payRate.recommendedRate.toFixed(2)}
              value={data.payPerVisit}
              onChange={(e) => {
                handlePayChange(e.target.value);
              }}
              sx={inputSx}
              inputProps={{
                min: rates.payRate.minimum,
                max: rates.payRate.maximum,
                step: 0.01,
              }}
            />
          </Stack>
        </>
      )}
    </Stack>
  );
}
